import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

// MUI
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import IconButton from '@mui/material/IconButton';
import Fab from "@mui/material/Fab";
import { ImageList, ImageListItem, ImageListItemBar, Skeleton, SxProps } from "@mui/material";

// MUI Icons
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from '@mui/icons-material/StarBorder';

// Custom Components
import modelImage from "@/models/images";
import dataController from "@/lib/dataController";
import arrayBufferToBase64 from "@/lib/arrayBufferToBase64";
import imgService from "@/services/imgService";
import ObjectAnalysisThumbsFilter from './ObjectAnalysisThumbsFilter';

//Types
import { DCRecord } from "@/@types/lib/dataController";

import Image from "@/images/brokent.jpg";

// const THUMB_WIDTH = 300;
const THUMB_HEIGHT = 225;
const REDDOT_RADIUS = 5;
type _WKT = {
  [key: number]: string;
}
const WKTS: _WKT = {
  0: 'POINT M(503200.5447 5133053.6776 135)',
  21: 'POINT M(503217.8794 5133052.9634 -135)',
  32: 'POINT M(503219.3811 5133039.7263 -45)',
  43: 'POINT M(503202.0464 5133038.4404 45)',
};

export type ThumbBasicInfo = {
  objektId: number;
  imageId: number;
  ordinal: number;
  wkt: string | null;
}

export type ThumbFilters = {
  textFilter: string;
  selectedFilter: boolean;
  notSelectedFiter: boolean;
  redDotsFilter: boolean;
  freeFlightFilter: boolean;
}

type ObjectAnalysisThumbsProps = {
  baseRecordPath: string,
  recordId: number | null;
  imagesBasicInfo: DCRecord[];
  selectedImage: DCRecord | null;
  selectedImageId: number;
  selectedImageInfo: DCRecord | null;
  onImageSelect: (id: number, image: DCRecord | null) => void;
  setThumbsLoaded: (thumsLoaded: boolean) => void;
  thumbsLoaded: boolean;
  isDisabled: boolean;
  handleMarkFavorite: (img: DCRecord) => void;
  toggleSwitchToTable?: () => void;
  hidden: boolean;
}

type IThumbsDictionary = {
  [key: number]: string
}

const initialFilterObject: ThumbFilters = {
  textFilter: "",
  selectedFilter: false,
  notSelectedFiter: false,
  redDotsFilter: true,
  freeFlightFilter: false
}

function ObjectAnalysisThumbs(props: ObjectAnalysisThumbsProps) {
  const [filteredRecords, setFilteredRecords] = useState<DCRecord[]>([]);
  const [filterObject, setFilterObject] = useState<ThumbFilters>(initialFilterObject)
  const [thumbs, setThumbs] = useState<IThumbsDictionary>({});
  var thumbCache = useRef<IThumbsDictionary>({});

  const { t } = useTranslation();
  const imageListRef = useRef<HTMLUListElement | null>(null);

  const {
    baseRecordPath,
    recordId,
    imagesBasicInfo,
    selectedImage,
    selectedImageId,
    onImageSelect,
    selectedImageInfo,
    setThumbsLoaded,
    thumbsLoaded,
    isDisabled,
    handleMarkFavorite,
    toggleSwitchToTable,
    hidden
  } = props;

  useEffect(() => {
    setThumbsLoaded(false);
    if (selectedImageId !== -1 && Object.keys(thumbs).length > 0) {
      const imgNo = imagesBasicInfo.findIndex(r => (r.id as number) === selectedImageId);
      if (imgNo !== -1) {
        onImageSelect(selectedImageId, imagesBasicInfo[imgNo]);
        scrollToSelected()
      }
    }
    if (Object.keys(thumbs).length > 0) {
      setThumbsLoaded(true);
    }
  }, [thumbs, selectedImageId, thumbCache]);

  useEffect(() => {
    if (filteredRecords.length > 0) {
      // setThumbsLoaded(true);
      handleScroll();
    }
  }, [filteredRecords])

  useEffect(() => {
    if (imagesBasicInfo.length) {
      filterThumbnails()
    } else {
      setFilteredRecords(prev => prev.length ? [] : prev);
    }
    //setThumbs(prev => Object.keys(prev).length ? {} : prev);
  }, [filterObject, imagesBasicInfo])

  const scrollToSelected = () => {
    const element = document.getElementById(selectedImage?.id as string);
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }

  useEffect(() => {
    scrollToSelected()
    if (selectedImageInfo !== null) {
      //select image
      const rec = imagesBasicInfo.find(x => x.id === selectedImageInfo.imageId);
      if (rec) {
        onImageSelect(rec.id as number, rec);
        navigator.clipboard.writeText(rec.path as string);
      }
    }
  }, [selectedImageInfo]);

  useEffect(() => {
    scrollToSelected()
  }, [selectedImage]);

  function filterThumbnails() {
    const newFilteredRecords = imagesBasicInfo.filter((record: any) => {
      let textOk = true;
      let selectedOk = true;
      let notSelectedOk = true;
      let redDotsOk = true;
      let freeFlightOk = true;

      // check text filter
      if (filterObject.textFilter.length > 0) {
        if (!(record.path.toLowerCase().includes(filterObject.textFilter))) {
          textOk = false;
        }
      }

      //check selected filter
      if (filterObject.selectedFilter === true && record.selected === false) {
        selectedOk = false;
      }

      //check not selected filter
      if (filterObject.notSelectedFiter === true && record.selected === true) {
        notSelectedOk = false;
      }

      //check red dots filters
      if (filterObject.redDotsFilter === true && (record.objpx === null || record.objpy === null)) {
        redDotsOk = false;
      }

      //check free flight filter
      if (filterObject.freeFlightFilter === true && !(record.path.includes("FF"))) {
        freeFlightOk = false;
      }

      return textOk && selectedOk && notSelectedOk && redDotsOk && freeFlightOk
    })
    setFilteredRecords(newFilteredRecords);
  }

  const downloadThumbs = () => {
    if (Array.isArray(imagesBasicInfo)) {
      const promises = imagesBasicInfo.map(r => {
        if (recordId && r.id && typeof r.id === 'number') {
          return imgService.getRecordImageThumb(baseRecordPath, recordId, r.id)
            .then(b64 => ([r.id as number, b64]))
            .catch(() =>
              fetch(Image, {
                method: "GET",
                mode: "cors",
              })
                .then(data => data.blob()
                  .then((blob: Blob) => blob.arrayBuffer()
                    .then((arrBuffer: ArrayBuffer) =>
                      ([r.id as number, arrayBufferToBase64(arrBuffer)])
                    ))));
        }
        return Promise.resolve([-1, null]);
      })
      Promise.all(promises).then(thumbs => {
        const _thumbs = thumbs.reduce((acc: IThumbsDictionary, curr) => {
          const [id, b64] = curr;
          if (id !== -1) {
            acc[id] = b64;
          }
          thumbCache.current[id] = b64
          return acc;
        }, {});
        setThumbs(_thumbs);
      })
    }
  };

  const downloadThumb = (thumbId: number) => {
    if (!Object.keys(thumbCache.current).includes(thumbId.toString())) {
      const promise = imgService.getRecordImageThumb(baseRecordPath, thumbId, thumbId)
        .then(b64 => ([thumbId, b64]))
        .catch(() =>
          fetch(Image, {
            method: "GET",
            mode: "cors",
          })
            .then(data => data.blob()
              .then((blob: Blob) => blob.arrayBuffer()
                .then((arrBuffer: ArrayBuffer) =>
                  ([thumbId, arrayBufferToBase64(arrBuffer)])
                ))));

      promise.then(thumb => {
        var _thumbs: IThumbsDictionary = {};
        Object.keys(thumbCache.current).forEach((thumbId) => {
          _thumbs[parseInt(thumbId)] = thumbCache.current[parseInt(thumbId)]
        });
        _thumbs[thumb[0]] = thumb[1]
        thumbCache.current[thumb[0]] = thumb[1]
        setThumbs(_thumbs);
      })
    }
  };


  const handleScroll = useCallback(() => {
    if (imageListRef.current) {
      const distanceScrolled = imageListRef.current?.scrollLeft;
      let shownThumbs: string[] = []
      imagesBasicInfo.forEach((value) => {
        const elementDistance = document.getElementById((value.id as string) + "-list-item")?.offsetLeft || -5030
        if (elementDistance > distanceScrolled - 600 && distanceScrolled + 3000 > elementDistance) {
          shownThumbs.push(value.imageId as string)
        }
      })
      shownThumbs.forEach((thumbId) => {
        if (!Object.keys(thumbCache.current).includes(thumbId.toString())) {
          downloadThumb(parseInt(thumbId))
          thumbCache.current[parseInt(thumbId)] = "";
        }
      })
    }
  }, [imagesBasicInfo]);

  useEffect(() => {
    if (Object.keys(thumbCache.current).length > 5000) thumbCache.current = {};
    document.getElementById("imagelist")?.addEventListener('scroll', handleScroll);
    handleScroll()
    return () => document.getElementById("imagelist")?.removeEventListener('scroll', handleScroll);
  }, [imagesBasicInfo, hidden]);

  const handleThumbnailClick = useCallback((rec: DCRecord) => {
    onImageSelect(rec.id as number, rec);
    downloadThumb(rec.id as number)
    navigator.clipboard.writeText(rec.path as string);
  }, []);

  const buildDataUrl = (imageId: number) => {
    if (thumbs[imageId] !== undefined) {
      return `data:image/jpg;base64,${thumbs[imageId]}`
    } else {
      return ''
    }
  }

  // doesnt do anything?
  let dravWidth = 240;
  const navWidth = (window.outerWidth - (dravWidth + 300))

  const selectedImageBorderStyle: SxProps = {
    border: 5,
    borderColor: "primary.main"
  }

  const selectedImageInfoBorderStyle: SxProps = {
    border: 5,
    borderColor: "rgba(255, 0, 255, 0.8)",
  }

  if (hidden) return (<></>);
  else return (
    // <div style={{ height: "100%", overflowX: "auto", backgroundColor: "#0F0"}}>
    <Paper elevation={0}>
      <Box m={0} p={0} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'start' }}>
        <ObjectAnalysisThumbsFilter
          filterObject={filterObject}
          onFilterChange={setFilterObject}
          recordsLength={imagesBasicInfo.length}
          filteredRecrodsLength={filteredRecords.length}
        />
        <IconButton onClick={() => downloadThumbs()}>
          <CloudDownloadIcon />
        </IconButton>
        {/* <Button variant="outlined" size={"small"} onClick={toggleSwitchToTable}>{t("objekti.view_kn_table")}</Button> */}
      </Box>
      <ImageList
        id="imagelist"
        cols={filteredRecords.length}
        ref={imageListRef}
        sx={{
          width: "inherit"
          , overflowX: "auto"
          , overflowY: "hidden"
          , display: "block"
          , height: filteredRecords.length ? 'fit-content' : 255
          , whiteSpace: "nowrap"
          , maxWidth: navWidth
        }}
      >
        {filteredRecords.map(x => {
          if (x.thumb_path !== undefined && x.serial !== undefined) {
            // conversion of px/py from original photo to thumbnail
            // px - distance from the left edge of photo to reddot
            // py - distance from the top edge of photo to reddot
            // using the ratio of x_thumbnail/x_original we calculate px_thumbanail
            // the REDDOT_RADIUS / 2 is here because we want the center of the reddot 
            // to hit the coordinates and not the top left corner
            const IMG_ASPECT_RATIO = x.width && x.height ? (x.width as number) / (x.height as number) : 300/225 // the fallback shouldnt happen
            const THUMB_WIDTH = IMG_ASPECT_RATIO * THUMB_HEIGHT;
            const px = x.objpx && x.width ? Math.round(x.objpx as number * (THUMB_WIDTH / (x.width as number)) - (REDDOT_RADIUS / 2)) : null;
            // px_border accounts for the border size when image is selected
            const px_border = x.objpx && x.width ? Math.round(x.objpx as number * ((THUMB_WIDTH - 10) / (x.width as number)) - (REDDOT_RADIUS / 2)) : null;
            const py = x.objpy && x.height ? Math.round(x.objpy as number * (THUMB_HEIGHT / (x.height as number)) - (REDDOT_RADIUS / 2)) : null;
            const py_border = x.objpy && x.height ? Math.round(x.objpy as number * ((THUMB_HEIGHT - 10) / (x.height as number)) - (REDDOT_RADIUS / 2)) : null;

            return (
              <ImageListItem
                id={x.id + "-list-item"}
                key={x.id as string}
                onClick={() => handleThumbnailClick(x)}
                sx={Object.assign(
                  {
                    width: THUMB_WIDTH
                    , height: THUMB_HEIGHT
                    , display: "inline-block"
                    , mr: 1
                  },
                  selectedImage && selectedImage.thumb_path === x.thumb_path
                    ? { ...selectedImageBorderStyle }
                    // : selectedImageInfo && selectedImageInfo.imageId === x.id
                    // ? { ...selectedImageInfoBorderStyle }
                    : {}
                )}
              >
                {
                  thumbs[x.id as number] === undefined || thumbs[x.id as number] === ""
                    ? (
                      <Skeleton
                        id={x.id as string}
                        animation={false}
                        variant="rectangular"
                        width={selectedImage && selectedImage.thumb_path === x.thumb_path ? 290 : 300}
                        height={selectedImage && selectedImage.thumb_path === x.thumb_path ? 220 : 230}
                      />
                    )
                    : (
                      <img
                        id={x.id as string}
                        src={buildDataUrl(x.id as number)}
                        alt={x.serial as string}
                        width={300}
                        height={230}
                      />
                    )
                }
                {
                  thumbs[x.id as number] !== undefined || thumbs[x.id as number] !== ""
                    ? (
                      <Fab
                        color="primary"
                        size="small"
                        sx={{ position: "absolute", top: 5, right: 5 }}
                      >
                        {x.ordinal}
                      </Fab>
                    )
                    : null
                }
                {
                  thumbs[x.id as number] !== undefined && px && py || thumbs[x.id as number] !== ""
                    ? (
                      <Box
                        m={0}
                        sx={{
                          position: "absolute",
                          zIndex: 10,
                          width: 0,
                          height: 0,
                          border: REDDOT_RADIUS + "px solid red",
                          borderRadius: REDDOT_RADIUS + "px",
                          left: selectedImage && selectedImage.thumb_path === x.thumb_path ? px_border : px + "px",
                          top: selectedImage && selectedImage.thumb_path === x.thumb_path ? py_border : py + "px",
                          display: px !== null && py !== null ? "block" : "none"
                        }}
                      />
                    )
                    : null
                }

                <ImageListItemBar
                  title={ 
                    x.path && String(x.path) && String(x.path).substring(String(x.path).lastIndexOf('/') + 1) ?
                      // is there always a subfolder ?
                      x.id + "  " +
                      String(x.path).split('/')[String(x.path).split('/').length - 2] + '/' +
                      String(x.path).substring(String(x.path).lastIndexOf('/') + 1) : null
                  }
                  sx={{
                    ".MuiImageListItemBar-title": {
                      fontSize: "9px"
                    }
                  }}
                  actionIcon={
                    <IconButton
                      sx={{ color: 'white' }}
                      aria-label={`star ${x.path}`}
                      onClick={(evt) => {
                        evt.stopPropagation();
                        evt.preventDefault();
                        handleMarkFavorite(x)
                      }}
                      disabled={isDisabled}
                    >
                      {x.selected ? <StarIcon id={x.id as string} /> :
                        <StarBorderIcon id={x.id as string} />}
                    </IconButton>
                  }
                  actionPosition="right"
                />

              </ImageListItem>
            )
          } else {
            return null;
          }

        })}
      </ImageList>
    </Paper>
    // </div>
  )

}

export default ObjectAnalysisThumbs

